import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import uuidv4 from 'uuid/v4';
import { SearchSuggestionsService } from '~content/services/SearchSuggestionsService';

import {
    ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, Chip, ChipPicker,
    ListItem, TabStyles
} from '@tableau/widgets';

import { selectAvailableActionsForEntity } from '../../../app/selectors/EntityActionsSelectors';
import { MapStateToProps } from '../../../app/utils/ConnectUtils';
import { Messages } from '../../../codegen/Localize';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { ISetTagsAction } from '../../../ts/ServerApi/types/actionNames';
import { ITag } from '../../../ts/ServerApi/types/ITag';
import { IMetricInfo } from '../../models/IMetricInfo';
import { Metrics } from '../../resources/Metrics';
import {
    editBlockStyleClass, editButtonClass, metadataSectionStyle, metadataSubTitle, metadataText,
    resolveButton, resolveButtonContainer
} from './MetricsStyles';

const metricsTagChipPickerLabel = 'metric-tag-chip-picker-label';

interface MetricTagsSectionOwnProps {
  readonly metricInfo: IMetricInfo;
}

interface MetricTagsSectionStateProps {
  readonly canEditTags: boolean;
}

interface MetricTagsSectionState {
  suggestedTags: ListItem[];

  displayedTags: string[];
  savedTags: string[];

  isSaving: boolean;
  isEditingTags: boolean;
}

export type MetricTagsSectionProps = MetricTagsSectionOwnProps & MetricTagsSectionStateProps;

export class MetricTagsSection extends React.PureComponent<MetricTagsSectionProps, MetricTagsSectionState> {
  public static readonly TAG_SUGGESTIONS_DEBOUNCE_MILLISECONDS = 250;
  private latestSuggestedTagsRequestId: string = null;
  private buttonRef: React.RefObject<any>;

  public constructor(props: MetricTagsSectionProps) {
    super(props);
    this.state = {
      suggestedTags: [],
      displayedTags: this.props.metricInfo.tags,
      savedTags: this.props.metricInfo.tags,
      isSaving: false,
      isEditingTags: false,
    };
    this.buttonRef = React.createRef();
  }

  private getSuggestedTagsDebounced = _.debounce(async (inputText: string, currentRequestId: string) => {
    const MAX_NUMBER_OF_SUGGESTIONS = 3;

    try {
      const result: ListResult<ITag> = await SearchSuggestionsService.tags(inputText).getSlice(0, MAX_NUMBER_OF_SUGGESTIONS);

      // If we resolve an old request then return early and allow the newer request to complete.
      if (this.latestSuggestedTagsRequestId !== currentRequestId) return;

      const suggestedTags = result.items.map(i => ({ value: i.name, disabled: false }));

      // We can not guarantee that an existing tag matching the input text will appear as the
      // first element in the suggested tags array. We need it to be the first element in order
      // to automatically select the value (by hitting the enter key). To get around this we remove
      // the matching tag and then add it back as the first element.
      const matchingTagIndex = suggestedTags.findIndex(tag => tag.value === inputText);
      if (matchingTagIndex > -1) {
        suggestedTags.splice(matchingTagIndex, 1);
      }

      suggestedTags.unshift({ value: inputText, disabled: false });

      this.setState({ suggestedTags });
    } catch {
      // Fail silently
    }
  }, MetricTagsSection.TAG_SUGGESTIONS_DEBOUNCE_MILLISECONDS);

  private getSuggestedTags = (inputText: string) => {
    // The request ID must be created outside of the debounced function
    // to prevent old requests from overriding the current input text.
    const currentRequestId = uuidv4();
    this.latestSuggestedTagsRequestId = currentRequestId;

    this.getSuggestedTagsDebounced(inputText, currentRequestId);
  }

  private getFilteredTags = (inputText: string) => {
    inputText = inputText.trim();

    if (!inputText) {
      this.setState({ suggestedTags: [] });
      return;
    }

    // Before making a network request to retrieve suggested tags make sure
    // we at least have one suggestion, which is the input text itself.
    this.setState({ suggestedTags: [{ value: inputText, disabled: false }] });

    this.getSuggestedTags(inputText);
  }

  private handleValueAdd = (value: string, index: number) => {
    value = value.trim();

    if (!value) return;

    // Handle duplicates
    if (this.state.displayedTags.find((tag) => tag === value) !== undefined) return;

    const newTags = [...this.state.displayedTags];
    newTags.splice(index, 0, value);
    this.setState({ displayedTags: newTags });
  }

  private handleValuesChange = (selectedValues: string[]) => {
    this.setState({ displayedTags: selectedValues, suggestedTags: [] });
  }

  private handleCancel = () => {
    this.setState({
      isEditingTags: false,
      displayedTags: this.state.savedTags,
    });
  }

  private handleSave = () => {
    if (this.state.isSaving) return;

    this.setState({
      isSaving: true,
    }, this.saveTagsAndStopEditing);
  }

  private saveTagsAndStopEditing = async () => {
    // Calculate tags to add/remove, then add quotes around each tag.
    // The quotes are needed for multi-word tags with spaces.
    const tagsToAdd = this.state.displayedTags.filter((tag) => {
      return this.state.savedTags.indexOf(tag) === -1;
    }).map(tag => `"${tag}"`);

    const tagsToRemove = this.state.savedTags.filter((tag) => {
      return this.state.displayedTags.indexOf(tag) === -1;
    }).map(tag => `"${tag}"`);

    try {
      const result: IResult = await Metrics.changeTags([this.props.metricInfo.id], tagsToAdd, tagsToRemove);

      this.setState({
        isEditingTags: false,
        isSaving: false,
        savedTags: this.state.displayedTags,
      });
      Metrics.toast(result, Messages.metricsMetadataPanelEditTagsSuccessful(), Messages.metricsMetadataPanelEditTagsFailure());
    } catch {
      this.setState({
        isEditingTags: true,
        isSaving: false,
      });
    }
  }

  private handleChangeCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent quotation marks and commas
    let sanitizedString = event.target.value;
    sanitizedString = sanitizedString.replace(/[",]/g, '');

    // Enforce character limit
    if (sanitizedString.length > 255) {
      sanitizedString = sanitizedString.slice(0, 255);
    }

    event.target.value = sanitizedString;
  }

  private renderTags = () => {
    if (this.state.isEditingTags) {
      return (
        <div>
          <ChipPicker
            disabled={this.state.isSaving}
            aria-labelledby={metricsTagChipPickerLabel}
            label=""
            style={chipPickerStyle}
            placeholder={Messages.tagsAddTagPrompt()}
            testId="metadata-tag-picker"
            listItems={this.state.suggestedTags}
            onValueAdd={this.handleValueAdd}
            onValuesChange={this.handleValuesChange}
            onFilterItems={this.getFilteredTags}
            onChangeCapture={this.handleChangeCapture}
          >
            {this.state.displayedTags.map((value: string, i: number) =>
              <Chip
                key={value}
                tooltip={value}
                value={value}
              >
                {value}
              </Chip>
            )}
          </ChipPicker>
          <div style={resolveButtonContainer} ref={this.buttonRef}>
            <ButtonWidget
              disabled={this.state.isSaving}
              style={resolveButton}
              buttonType={ButtonType.Outline}
              onClick={this.handleCancel}
              testId="cancel-tags-edit">
              {Messages.cancel()}
            </ButtonWidget>
            <ButtonWidget style={resolveButton} buttonType={ButtonType.Primary} onClick={this.handleSave} testId="save-tags">
              {
                this.state.isSaving ?
                  <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                    <ActivitySpinnerWidget onDarkBackground spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                  </div> :
                  <span>{Messages.save()}</span>
              }
            </ButtonWidget>
          </div>
        </div>
      );
    } else {
      const textToDisplay = this.state.displayedTags.length > 0 ? this.state.displayedTags.join(', ') : Messages.metricsMetadataPanelNoTags();
      return (
        <div data-tb-test-id={'metadata-tags'} style={metadataText}>{textToDisplay}</div>
      );
    }
  }

  private scrollSaveButtonIntoView(): void {
    this.buttonRef.current.scrollIntoView();
  }

  public render(): JSX.Element {
    const handleEditClick = () => {
      this.setState({ isEditingTags: true }, this.scrollSaveButtonIntoView);
    };

    const canEnterEditModeWithClick = this.props.canEditTags && !this.state.isEditingTags;
    const editModeHandler = canEnterEditModeWithClick ? handleEditClick : undefined;

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div
        className={[metadataSectionStyle, canEnterEditModeWithClick ? editBlockStyleClass : ''].join(' ')}
        onClick={editModeHandler}
        data-tb-test-id={'metadata-tags-container'}
      >
        <div style={{display: 'flex'}}>
          <div id={metricsTagChipPickerLabel} style={metadataSubTitle}>{Messages.metricsMetadataPanelTags()}</div>
          {canEnterEditModeWithClick ?
            <button className={editButtonClass} aria-label={Messages.metricsMetadataPanelEditTagsLabel()} onClick={editModeHandler} />
            : null
          }
        </div>
        {this.renderTags()}
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<MetricTagsSectionStateProps, MetricTagsSectionOwnProps> = (state, ownProps) => {
  const availableActions = selectAvailableActionsForEntity(state, ownProps.metricInfo);
  return {
    canEditTags: availableActions && (availableActions as ISetTagsAction).setTags,
  };
};

const ConnectedMetricTagsSection: React.ComponentClass<MetricTagsSectionOwnProps>
  = connect(mapStateToProps, undefined)(MetricTagsSection);

export default ConnectedMetricTagsSection;

const chipPickerStyle: React.CSSProperties = {
  marginBottom: '12px',
};
